import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Sidebar from '../../components/Sidebar';
import TopNav from '../../components/TopNav';
import { Badge, Button, Card, Modal, Spinner, StatCard } from '../../components/ui';
import { platformService } from '../../services/api';
import { getApiErrorMessage } from '../../utils/apiErrors';


const EMPTY_FORM = {
  name: '', slug: '', subdomain: '', custom_domain: '', plan_definition: '',
  max_users: 1000, max_storage_gb: 25, locale: 'en', timezone: 'Africa/Algiers',
  license_expires_at: '',
  admin_email: '', admin_password: '', admin_first_name: '', admin_last_name: '', notes: '',
  features: {}, quotas: {},
  automatic_backups: true, backup_frequency_hours: 24, backup_retention_days: 14,
};

const EMPTY_PLAN = {
  name: '', code: '', description: '', max_users: 1000, max_storage_gb: 25,
  license_days: 365, is_active: true,
  features: {}, quotas: {},
};

const STATUS_TONES = {
  active: 'green', provisioning: 'yellow', suspended: 'red', archived: 'gray', failed: 'red',
};

function Field({ label, required, hint, children }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold text-gray-700">{label}{required && <span className="text-red-500"> *</span>}</span>
      <div className="mt-1.5">{children}</div>
      {hint && <span className="mt-1 block text-xs text-gray-400">{hint}</span>}
    </label>
  );
}

const inputClass = 'w-full rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm text-gray-900 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100';

function formatBytes(bytes) {
  const number = Number(bytes);
  if (!Number.isFinite(number)) return '—';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const index = Math.min(Math.floor(Math.log(Math.max(number, 1)) / Math.log(1024)), units.length - 1);
  return `${(number / (1024 ** index)).toFixed(index > 1 ? 1 : 0)} ${units[index]}`;
}

function toLocalDateTime(value) {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  const local = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
  return local.toISOString().slice(0, 16);
}

function toApiDateTime(value) {
  return value ? new Date(value).toISOString() : null;
}

function entitlementDefaults(catalogue) {
  return Object.fromEntries((catalogue.entitlements || []).map((item) => [item.key, item.default_enabled !== false]));
}

function normalizedFeatures(features, catalogue) {
  return { ...entitlementDefaults(catalogue), ...(features || {}) };
}

function normalizedQuotas(quotas, catalogue) {
  return Object.fromEntries((catalogue.quotas || []).map((item) => [
    item.key, quotas?.[item.key] ?? '',
  ]));
}

function quotaPayload(quotas, catalogue) {
  return Object.fromEntries((catalogue.quotas || []).map((item) => [
    item.key, quotas?.[item.key] === '' || quotas?.[item.key] == null ? null : Number(quotas[item.key]),
  ]));
}

function planDefaults(plan, catalogue) {
  if (!plan) return {};
  const expires = plan.license_days
    ? new Date(Date.now() + (Number(plan.license_days) * 86400000))
    : null;
  return {
    plan_definition: String(plan.id),
    max_users: plan.max_users,
    max_storage_gb: plan.max_storage_gb,
    license_expires_at: toLocalDateTime(expires),
    features: normalizedFeatures(plan.features, catalogue),
    quotas: normalizedQuotas(plan.quotas, catalogue),
  };
}

function QuotaFields({ catalogue, quotas, onChange, t, usage = null, readOnly = false }) {
  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {(catalogue.quotas || []).map((item) => {
        const current = usage?.[item.key];
        const limit = quotas?.[item.key];
        const percent = current != null && limit !== '' && limit != null && Number(limit) > 0
          ? Math.min(100, (Number(current) / Number(limit)) * 100) : 0;
        return (
          <Field key={item.key} label={t(item.label)} hint={current == null ? t(item.description) : `${current} ${t('used')} · ${limit === '' || limit == null ? t('Unlimited') : limit}`}>
            <input type="number" min="0" readOnly={readOnly} placeholder={t('Unlimited')} value={limit ?? ''} onChange={(event) => onChange(item.key, event.target.value)} className={`${inputClass} ${readOnly ? 'bg-gray-50' : ''}`} />
            {current != null && limit !== '' && limit != null && <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-gray-100"><div className={`h-full rounded-full ${percent >= 90 ? 'bg-red-500' : percent >= 70 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${percent}%` }} /></div>}
          </Field>
        );
      })}
    </div>
  );
}

function EntitlementFields({ catalogue, features, onChange, t, compact = false }) {
  return (
    <div className="space-y-4">
      {(catalogue.categories || []).map((category) => {
        const items = (catalogue.entitlements || []).filter((item) => item.category === category.key);
        if (!items.length) return null;
        return (
          <section key={category.key} className="rounded-2xl border border-gray-200 bg-white p-4">
            <h4 className="text-sm font-bold text-gray-900">{t(category.label)}</h4>
            <div className={`mt-3 grid gap-2 ${compact ? 'grid-cols-1' : 'sm:grid-cols-2 lg:grid-cols-3'}`}>
              {items.map((item) => (
                <label key={item.key} className="flex cursor-pointer items-start gap-3 rounded-xl border border-gray-100 bg-gray-50 p-3 transition hover:border-primary-200 hover:bg-primary-50/40">
                  <input type="checkbox" className="mt-0.5" checked={features?.[item.key] !== false} onChange={(event) => onChange(item.key, event.target.checked)} />
                  <span><span className="block text-sm font-semibold text-gray-800">{t(item.label)}</span>{!compact && <span className="mt-0.5 block text-xs leading-5 text-gray-500">{t(item.description)}</span>}</span>
                </label>
              ))}
            </div>
          </section>
        );
      })}
    </div>
  );
}

function SchoolCard({ school, busy, onAction, onSelect, t }) {
  const health = school.last_health_details || {};
  const operation = (school.operations || []).find((item) => ['pending', 'running'].includes(item.status));
  return (
    <Card className="relative overflow-hidden p-0">
      <div className={`h-1.5 ${school.status === 'active' ? 'bg-emerald-500' : school.status === 'failed' ? 'bg-red-500' : school.status === 'suspended' ? 'bg-amber-500' : 'bg-gray-300'}`} />
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <button type="button" onClick={() => onSelect(school)} className="min-w-0 text-left rtl:text-right">
            <h3 className="truncate text-lg font-bold text-gray-900">{school.name}</h3>
            <p className="mt-1 truncate text-sm text-gray-500">{school.primary_host || school.subdomain || school.custom_domain}</p>
          </button>
          <Badge tone={STATUS_TONES[school.status]}>{t(school.status)}</Badge>
        </div>
        <div className="mt-5 grid grid-cols-3 gap-2 rounded-xl bg-gray-50 p-3 text-center">
          <div><p className="text-lg font-bold text-gray-900">{health.users ?? '—'}</p><p className="text-[11px] text-gray-500">{t('Users')}</p></div>
          <div><p className="text-lg font-bold text-gray-900">{health.courses ?? '—'}</p><p className="text-[11px] text-gray-500">{t('Courses')}</p></div>
          <div><p className="text-lg font-bold text-gray-900">{formatBytes(health.database_size_bytes)}</p><p className="text-[11px] text-gray-500">{t('Database')}</p></div>
        </div>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Badge tone="purple">{school.plan_details?.name || t(school.plan)}</Badge>
          <span className="text-xs text-gray-400">{school.max_users.toLocaleString()} {t('user limit')}</span>
          <Badge tone={school.license_status === 'expired' ? 'red' : school.license_status === 'expiring' ? 'yellow' : 'green'}>{t(school.license_status)}</Badge>
          {school.last_health_status && <Badge tone={school.last_health_status === 'healthy' ? 'green' : 'red'}>{t(school.last_health_status)}</Badge>}
        </div>
        {school.provisioning_error && <p className="mt-3 line-clamp-2 rounded-lg bg-red-50 p-2 text-xs text-red-700">{school.provisioning_error}</p>}
        {operation && <div className="mt-3 rounded-xl bg-primary-50 p-3"><div className="flex justify-between text-xs font-semibold text-primary-800"><span>{t(operation.operation)}</span><span>{operation.progress || 0}%</span></div><div className="mt-2 h-1.5 overflow-hidden rounded-full bg-primary-100"><div className="h-full rounded-full bg-primary-600 transition-all" style={{ width: `${operation.progress || 2}%` }} /></div></div>}
        <div className="mt-5 flex flex-wrap gap-2 border-t border-gray-100 pt-4">
          {school.status === 'active' && school.primary_host && (
            <a href={`https://${school.primary_host}`} target="_blank" rel="noreferrer" className="inline-flex items-center rounded-lg bg-primary-50 px-3 py-1.5 text-xs font-semibold text-primary-700 hover:bg-primary-100">{t('Open school')}</a>
          )}
          <Button size="sm" variant="secondary" loading={busy === `health-${school.public_id}`} onClick={() => onAction('health', school)}>{t('Check health')}</Button>
          <Button size="sm" variant="secondary" loading={busy === `backup-${school.public_id}`} onClick={() => onAction('backup', school)}>{t('Back up')}</Button>
          <Button size="sm" variant="secondary" onClick={() => onAction('edit', school)}>{t('Configure')}</Button>
          {school.status === 'active' && !school.uses_default_database && <Button size="sm" variant="secondary" onClick={() => onAction('suspend', school)}>{t('Suspend')}</Button>}
          {school.status === 'suspended' && <Button size="sm" variant="success" onClick={() => onAction('activate', school)}>{t('Activate')}</Button>}
          {school.status === 'suspended' && <Button size="sm" variant="danger" onClick={() => onAction('archive', school)}>{t('Archive')}</Button>}
          {school.status === 'failed' && <Button size="sm" variant="primary" onClick={() => onAction('retry', school)}>{t('Retry')}</Button>}
          <Button size="sm" variant="ghost" loading={busy === `migrate-${school.public_id}`} onClick={() => onAction('migrate', school)}>{t('Migrate')}</Button>
        </div>
      </div>
    </Card>
  );
}

export default function PlatformSchools() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState(null);
  const [schools, setSchools] = useState([]);
  const [plans, setPlans] = useState([]);
  const [catalogue, setCatalogue] = useState({ categories: [], entitlements: [], quotas: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [details, setDetails] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState('');
  const [retrySchool, setRetrySchool] = useState(null);
  const [editSchool, setEditSchool] = useState(null);
  const [editForm, setEditForm] = useState(null);
  const [plansOpen, setPlansOpen] = useState(false);
  const [planForm, setPlanForm] = useState(EMPTY_PLAN);
  const [editingPlanId, setEditingPlanId] = useState(null);
  const [retryCredentials, setRetryCredentials] = useState({ admin_email: '', admin_password: '' });

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const [overviewResponse, schoolsResponse, plansResponse, catalogueResponse] = await Promise.all([
        platformService.getOverview(), platformService.getSchools({ search, status: statusFilter }),
        platformService.getPlans(), platformService.getEntitlements(),
      ]);
      setOverview(overviewResponse.data);
      setSchools(schoolsResponse.data.results || schoolsResponse.data || []);
      setPlans(plansResponse.data.results || plansResponse.data || []);
      setCatalogue(catalogueResponse.data);
      setError('');
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, 'Unable to load schools.'));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [search, statusFilter, t]);

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
  }, [load]);

  useEffect(() => {
    if (!schools.some((school) => (school.operations || []).some((operation) => ['pending', 'running'].includes(operation.status)))) return undefined;
    const timer = setInterval(() => load(true), 4000);
    return () => clearInterval(timer);
  }, [schools, load]);

  const tiles = useMemo(() => [
    { label: t('All schools'), value: overview?.total_schools ?? '—', color: 'blue' },
    { label: t('Active schools'), value: overview?.active_schools ?? '—', color: 'green' },
    { label: t('Needs attention'), value: (overview?.failed_schools || 0) + (overview?.suspended_schools || 0) + (overview?.expired_licenses || 0), color: 'red' },
    { label: t('Custom domains'), value: overview?.custom_domains ?? '—', color: 'purple' },
  ], [overview, t]);

  const updateForm = (key, value) => {
    setForm((current) => ({
      ...current,
      [key]: value,
      ...(key === 'name' && !current.slug ? {
        slug: value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50),
      } : {}),
    }));
  };

  const selectPlan = (planId, setter) => {
    const plan = plans.find((item) => String(item.id) === String(planId));
    setter((current) => ({ ...current, ...planDefaults(plan, catalogue) }));
  };

  const openCreate = () => {
    const preferred = plans.find((plan) => plan.code === 'standard' && plan.is_active) || plans.find((plan) => plan.is_active);
    setForm({ ...EMPTY_FORM, ...planDefaults(preferred, catalogue) });
    setCreateOpen(true);
  };

  const createSchool = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const response = await platformService.createSchool({
        ...form,
        subdomain: form.subdomain || null,
        custom_domain: form.custom_domain || null,
        plan_definition: Number(form.plan_definition),
        license_expires_at: toApiDateTime(form.license_expires_at),
        max_users: Number(form.max_users),
        max_storage_gb: Number(form.max_storage_gb),
        backup_frequency_hours: Number(form.backup_frequency_hours),
        backup_retention_days: Number(form.backup_retention_days),
        quotas: quotaPayload(form.quotas, catalogue),
      });
      setCreateOpen(false);
      setForm(EMPTY_FORM);
      setDetails(response.data);
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, 'Unable to create the school.'));
    } finally {
      setSaving(false);
    }
  };

  const performAction = async (actionName, school) => {
    if (actionName === 'edit') {
      setEditSchool(school);
      setEditForm({
        name: school.name,
        subdomain: school.subdomain || '', custom_domain: school.custom_domain || '',
        plan_definition: school.plan_definition ? String(school.plan_definition) : '',
        max_users: school.max_users, max_storage_gb: school.max_storage_gb,
        license_expires_at: toLocalDateTime(school.license_expires_at),
        locale: school.locale, timezone: school.timezone, notes: school.notes || '',
        features: normalizedFeatures(school.features, catalogue),
        quotas: normalizedQuotas(school.quotas, catalogue),
        automatic_backups: school.automatic_backups,
        backup_frequency_hours: school.backup_frequency_hours,
        backup_retention_days: school.backup_retention_days,
      });
      return;
    }
    if (actionName === 'retry') {
      setRetrySchool(school);
      setRetryCredentials({ admin_email: '', admin_password: '' });
      return;
    }
    if (actionName === 'suspend' && !window.confirm(t('Suspend this school? Its users will immediately lose access.'))) return;
    if (actionName === 'archive' && !window.confirm(t('Archive this school? Its database and files will be preserved.'))) return;
    setBusy(`${actionName}-${school.public_id}`);
    try {
      const methods = {
        health: platformService.checkSchoolHealth,
        migrate: platformService.migrateSchool,
        suspend: platformService.suspendSchool,
        activate: platformService.activateSchool,
        archive: platformService.archiveSchool,
        backup: platformService.backupSchool,
      };
      await methods[actionName](school.public_id);
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, 'The operation failed.'));
    } finally {
      setBusy('');
    }
  };

  const retry = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await platformService.retryProvisioning(retrySchool.public_id, retryCredentials);
      setRetrySchool(null);
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, 'Provisioning failed.'));
    } finally {
      setSaving(false);
    }
  };

  const saveConfiguration = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      await platformService.updateSchool(editSchool.public_id, {
        ...editForm,
        subdomain: editForm.subdomain || null,
        custom_domain: editForm.custom_domain || null,
        plan_definition: Number(editForm.plan_definition),
        license_expires_at: toApiDateTime(editForm.license_expires_at),
        max_users: Number(editForm.max_users),
        max_storage_gb: Number(editForm.max_storage_gb),
        backup_frequency_hours: Number(editForm.backup_frequency_hours),
        backup_retention_days: Number(editForm.backup_retention_days),
        quotas: quotaPayload(editForm.quotas, catalogue),
      });
      setEditSchool(null);
      setEditForm(null);
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, 'Unable to save school configuration.'));
    } finally {
      setSaving(false);
    }
  };

  const openPlans = () => {
    setEditingPlanId(null);
    setPlanForm({ ...EMPTY_PLAN, features: entitlementDefaults(catalogue), quotas: normalizedQuotas({}, catalogue) });
    setPlansOpen(true);
  };

  const editPlan = (plan) => {
    setEditingPlanId(plan.id);
    setPlanForm({
      name: plan.name, code: plan.code, description: plan.description || '',
      max_users: plan.max_users, max_storage_gb: plan.max_storage_gb,
      license_days: plan.license_days ?? '', is_active: plan.is_active,
      features: normalizedFeatures(plan.features, catalogue),
      quotas: normalizedQuotas(plan.quotas, catalogue),
    });
  };

  const savePlan = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...planForm,
        max_users: Number(planForm.max_users),
        max_storage_gb: Number(planForm.max_storage_gb),
        license_days: planForm.license_days === '' ? null : Number(planForm.license_days),
        quotas: quotaPayload(planForm.quotas, catalogue),
      };
      if (editingPlanId) await platformService.updatePlan(editingPlanId, payload);
      else await platformService.createPlan(payload);
      setEditingPlanId(null);
      setPlanForm({ ...EMPTY_PLAN, features: entitlementDefaults(catalogue), quotas: normalizedQuotas({}, catalogue) });
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, 'Unable to save the plan.'));
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async (plan) => {
    if (!window.confirm(t('Delete this plan?'))) return;
    setBusy(`plan-${plan.id}`);
    try {
      await platformService.deletePlan(plan.id);
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, 'Unable to delete the plan.'));
    } finally {
      setBusy('');
    }
  };

  const verifySelectedDomain = async () => {
    setBusy(`verify-${details.public_id}`);
    try {
      const response = await platformService.verifyDomain(details.public_id);
      setDetails((current) => ({ ...current, custom_domain_status: response.data.status }));
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, 'Domain verification failed.'));
    } finally {
      setBusy('');
    }
  };

  const restoreBackup = async (backup) => {
    const confirmation = window.prompt(t('Type {{slug}} to confirm restoring this backup. Current data will be replaced.', { slug: details.slug }));
    if (confirmation !== details.slug) return;
    setBusy(`restore-${backup.public_id}`);
    try {
      await platformService.restoreSchoolBackup(details.public_id, backup.public_id, confirmation);
      setDetails(null);
      await load();
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, t, 'Unable to restore the backup.'));
    } finally {
      setBusy('');
    }
  };

  return (
    <div className="flex h-screen bg-surface-50">
      <Sidebar />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <TopNav title={t('School Control Plane')} />
        <main className="flex-1 overflow-y-auto p-4 pb-24 sm:p-6 sm:pb-24">
          <div className="mx-auto max-w-7xl">
            <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-slate-950 via-primary-950 to-primary-700 p-7 text-white shadow-xl sm:p-9">
              <div className="absolute -right-20 -top-24 h-72 w-72 rounded-full bg-cyan-400/20 blur-3xl" />
              <div className="relative flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
                <div className="max-w-2xl">
                  <p className="text-xs font-bold uppercase tracking-[0.24em] text-primary-200">L’mcid Cloud</p>
                  <h1 className="mt-3 text-3xl font-bold sm:text-4xl">{t('Every school, completely separated.')}</h1>
                  <p className="mt-3 text-sm leading-6 text-slate-200 sm:text-base">{t('Provision databases, domains, administrators, limits and application capabilities from one secure control plane.')}</p>
                </div>
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Button variant="secondary" className="border-white/30 !bg-white/10 !text-white hover:!bg-white/20" onClick={openPlans}>{t('Manage plans')}</Button>
                  <Button className="bg-white !text-primary-800 hover:!bg-primary-50" onClick={openCreate}>{t('Create a school')}</Button>
                </div>
              </div>
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
              {tiles.map((tile) => <StatCard key={tile.label} {...tile} />)}
            </div>

            <div className="mt-6 flex flex-col gap-3 rounded-2xl bg-white p-4 shadow-card sm:flex-row">
              <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder={t('Search schools, subdomains or domains')} className={`${inputClass} flex-1`} />
              <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)} className={`${inputClass} sm:w-52`}>
                <option value="">{t('All statuses')}</option>
                {['active', 'provisioning', 'suspended', 'failed', 'archived'].map((value) => <option key={value} value={value}>{t(value)}</option>)}
              </select>
              <Button variant="secondary" onClick={load}>{t('Refresh')}</Button>
            </div>

            {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
            {loading ? <div className="grid min-h-56 place-items-center"><Spinner /></div> : schools.length === 0 ? (
              <Card className="mt-6 text-center"><h2 className="text-lg font-bold text-gray-900">{t('No schools found')}</h2><p className="mt-2 text-sm text-gray-500">{t('Create the first isolated school from this control plane.')}</p></Card>
            ) : (
              <div className="mt-6 grid gap-4 xl:grid-cols-2">
                {schools.map((school) => <SchoolCard key={school.public_id} school={school} busy={busy} onAction={performAction} onSelect={setDetails} t={t} />)}
              </div>
            )}
          </div>
        </main>
      </div>

      <Modal open={createOpen} onClose={() => !saving && setCreateOpen(false)} title={t('Create an isolated school')} size="xl">
        <form onSubmit={createSchool} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('School name')} required><input required value={form.name} onChange={(e) => updateForm('name', e.target.value)} className={inputClass} /></Field>
            <Field label={t('Internal identifier')} required hint={t('Used for the isolated database and cannot be changed later.')}><input required pattern="[a-z0-9-]+" value={form.slug} onChange={(e) => updateForm('slug', e.target.value.toLowerCase())} className={inputClass} /></Field>
            <Field label={t('L’mcid subdomain')} hint="school-name.lmcid.com"><div className="flex"><input value={form.subdomain} onChange={(e) => updateForm('subdomain', e.target.value.toLowerCase())} className={`${inputClass} rounded-r-none`} /><span className="grid place-items-center rounded-r-xl border border-l-0 border-gray-200 bg-gray-50 px-3 text-xs text-gray-500">.lmcid.com</span></div></Field>
            <Field label={t('Custom domain')} hint={t('Optional. Point its DNS to the L’mcid server, then verify it here.')}><input value={form.custom_domain} placeholder="school.example.com" onChange={(e) => updateForm('custom_domain', e.target.value.toLowerCase())} className={inputClass} /></Field>
            <Field label={t('Plan')} required hint={t('Selecting a plan fills its limits, licence and capabilities. You can customize them below.')}><select required value={form.plan_definition} onChange={(e) => selectPlan(e.target.value, setForm)} className={inputClass}><option value="">{t('Choose a plan')}</option>{plans.filter((plan) => plan.is_active).map((plan) => <option key={plan.id} value={plan.id}>{plan.name}</option>)}</select></Field>
            <Field label={t('Language')}><select value={form.locale} onChange={(e) => updateForm('locale', e.target.value)} className={inputClass}><option value="en">English</option><option value="fr">Français</option><option value="ar">العربية</option></select></Field>
            <Field label={t('Maximum users')}><input type="number" min="1" required value={form.max_users} onChange={(e) => updateForm('max_users', e.target.value)} className={inputClass} /></Field>
            <Field label={t('Storage limit (GB)')}><input type="number" min="1" required value={form.max_storage_gb} onChange={(e) => updateForm('max_storage_gb', e.target.value)} className={inputClass} /></Field>
            <Field label={t('Licence expires')} hint={t('Leave empty for an unlimited licence.')}><input type="datetime-local" value={form.license_expires_at} onChange={(e) => updateForm('license_expires_at', e.target.value)} className={inputClass} /></Field>
            <Field label={t('Backup frequency (hours)')}><input type="number" min="1" value={form.backup_frequency_hours} onChange={(e) => updateForm('backup_frequency_hours', e.target.value)} className={inputClass} /></Field>
            <Field label={t('Backup retention (days)')}><input type="number" min="1" value={form.backup_retention_days} onChange={(e) => updateForm('backup_retention_days', e.target.value)} className={inputClass} /></Field>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700"><input type="checkbox" checked={form.automatic_backups} onChange={(e) => updateForm('automatic_backups', e.target.checked)} />{t('Automatic backups')}</label>
          </div>
          <div className="rounded-2xl border border-primary-100 bg-primary-50/50 p-5">
            <h3 className="font-bold text-gray-900">{t('First school administrator')}</h3>
            <p className="mt-1 text-xs text-gray-500">{t('This is a regular school administrator, not a platform owner.')}</p>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <Field label={t('First name')}><input value={form.admin_first_name} onChange={(e) => updateForm('admin_first_name', e.target.value)} className={inputClass} /></Field>
              <Field label={t('Last name')}><input value={form.admin_last_name} onChange={(e) => updateForm('admin_last_name', e.target.value)} className={inputClass} /></Field>
              <Field label={t('Email')} required><input type="email" required value={form.admin_email} onChange={(e) => updateForm('admin_email', e.target.value)} className={inputClass} /></Field>
              <Field label={t('Temporary password')} required hint={t('At least 10 characters. It is never stored in the control plane.')}><input type="password" minLength="10" required value={form.admin_password} onChange={(e) => updateForm('admin_password', e.target.value)} className={inputClass} /></Field>
            </div>
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{t('Role and content limits')}</h3>
            <p className="mt-1 text-xs text-gray-500">{t('Leave a field empty for unlimited access. The overall user and storage limits still apply.')}</p>
            <div className="mt-3"><QuotaFields catalogue={catalogue} quotas={form.quotas} onChange={(key, value) => setForm((current) => ({ ...current, quotas: { ...current.quotas, [key]: value } }))} t={t} /></div>
          </div>
          <div>
            <h3 className="font-bold text-gray-900">{t('Enabled capabilities')}</h3>
            <p className="mt-1 text-xs text-gray-500">{t('Capabilities are grouped by purpose and enforced in both the interface and API.')}</p>
            <div className="mt-3"><EntitlementFields catalogue={catalogue} features={form.features} onChange={(key, checked) => setForm((current) => ({ ...current, features: { ...current.features, [key]: checked } }))} t={t} /></div>
          </div>
          <div className="flex justify-end gap-3"><Button variant="secondary" onClick={() => setCreateOpen(false)}>{t('Cancel')}</Button><Button type="submit" loading={saving}>{t('Provision school')}</Button></div>
        </form>
      </Modal>

      <Modal open={Boolean(editSchool)} onClose={() => !saving && setEditSchool(null)} title={t('Configure school')} size="xl">
        {editForm && <form onSubmit={saveConfiguration} className="space-y-6">
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label={t('School name')} required><input required value={editForm.name} onChange={(e) => setEditForm((v) => ({ ...v, name: e.target.value }))} className={inputClass} /></Field>
            <Field label={t('L’mcid subdomain')}><input value={editForm.subdomain} onChange={(e) => setEditForm((v) => ({ ...v, subdomain: e.target.value.toLowerCase() }))} className={inputClass} /></Field>
            <Field label={t('Custom domain')}><input value={editForm.custom_domain} onChange={(e) => setEditForm((v) => ({ ...v, custom_domain: e.target.value.toLowerCase() }))} className={inputClass} /></Field>
            <Field label={t('Plan')} required hint={t('Changing the plan resets the fields below to its defaults; you can then customize them.')}><select required value={editForm.plan_definition} onChange={(e) => selectPlan(e.target.value, setEditForm)} className={inputClass}><option value="">{t('Choose a plan')}</option>{plans.map((plan) => <option key={plan.id} value={plan.id} disabled={!plan.is_active}>{plan.name}{!plan.is_active ? ` (${t('inactive')})` : ''}</option>)}</select></Field>
            <Field label={t('Language')}><select value={editForm.locale} onChange={(e) => setEditForm((v) => ({ ...v, locale: e.target.value }))} className={inputClass}><option value="en">English</option><option value="fr">Français</option><option value="ar">العربية</option></select></Field>
            <Field label={t('Maximum users')}><input type="number" min="1" value={editForm.max_users} onChange={(e) => setEditForm((v) => ({ ...v, max_users: e.target.value }))} className={inputClass} /></Field>
            <Field label={t('Storage limit (GB)')}><input type="number" min="1" value={editForm.max_storage_gb} onChange={(e) => setEditForm((v) => ({ ...v, max_storage_gb: e.target.value }))} className={inputClass} /></Field>
            <Field label={t('Licence expires')} hint={t('Leave empty for an unlimited licence.')}><input type="datetime-local" value={editForm.license_expires_at} onChange={(e) => setEditForm((v) => ({ ...v, license_expires_at: e.target.value }))} className={inputClass} /></Field>
            <Field label={t('Timezone')}><input value={editForm.timezone} onChange={(e) => setEditForm((v) => ({ ...v, timezone: e.target.value }))} className={inputClass} /></Field>
            <Field label={t('Notes')}><textarea rows="3" value={editForm.notes} onChange={(e) => setEditForm((v) => ({ ...v, notes: e.target.value }))} className={inputClass} /></Field>
            <Field label={t('Backup frequency (hours)')}><input type="number" min="1" value={editForm.backup_frequency_hours} onChange={(e) => setEditForm((v) => ({ ...v, backup_frequency_hours: e.target.value }))} className={inputClass} /></Field>
            <Field label={t('Backup retention (days)')}><input type="number" min="1" value={editForm.backup_retention_days} onChange={(e) => setEditForm((v) => ({ ...v, backup_retention_days: e.target.value }))} className={inputClass} /></Field>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700"><input type="checkbox" checked={editForm.automatic_backups} onChange={(e) => setEditForm((v) => ({ ...v, automatic_backups: e.target.checked }))} />{t('Automatic backups')}</label>
          </div>
          <div><h3 className="font-bold text-gray-900">{t('Role and content limits')}</h3><p className="mt-1 text-xs text-gray-500">{t('These limits override the selected plan for this school only.')}</p><div className="mt-3"><QuotaFields catalogue={catalogue} quotas={editForm.quotas} usage={editSchool.last_health_details} onChange={(key, value) => setEditForm((current) => ({ ...current, quotas: { ...current.quotas, [key]: value } }))} t={t} /></div></div>
          <div><h3 className="font-bold text-gray-900">{t('Enabled capabilities')}</h3><p className="mt-1 text-xs text-gray-500">{t('These values override the selected plan for this school only.')}</p><div className="mt-3"><EntitlementFields catalogue={catalogue} features={editForm.features} onChange={(key, checked) => setEditForm((value) => ({ ...value, features: { ...value.features, [key]: checked } }))} t={t} /></div></div>
          <div className="flex justify-end gap-3"><Button variant="secondary" onClick={() => setEditSchool(null)}>{t('Cancel')}</Button><Button type="submit" loading={saving}>{t('Save changes')}</Button></div>
        </form>}
      </Modal>

      <Modal open={Boolean(details)} onClose={() => setDetails(null)} title={details?.name} size="lg">
        {details && <div className="space-y-5 text-sm">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {[['Status', details.status], ['Plan', details.plan_details?.name || details.plan], ['Licence status', details.license_status], ['Licence expires', details.license_expires_at ? new Date(details.license_expires_at).toLocaleString() : t('Unlimited')], ['Database', details.database_name], ['Schema', details.schema_version || '—'], ['User limit', details.max_users], ['Storage limit', `${details.max_storage_gb} GB`]].map(([label, value]) => <div key={label} className="rounded-xl bg-gray-50 p-3"><p className="text-xs text-gray-400">{t(label)}</p><p className="mt-1 break-all font-semibold text-gray-800">{t(String(value))}</p></div>)}
          </div>
          <div><h3 className="font-bold text-gray-900">{t('Live quota usage')}</h3><p className="mt-1 text-xs text-gray-500">{details.last_health_check_at ? `${t('Last checked')} ${new Date(details.last_health_check_at).toLocaleString()}` : t('Run a health check to load current usage.')}</p><div className="mt-3"><QuotaFields readOnly catalogue={catalogue} quotas={normalizedQuotas(details.quotas, catalogue)} usage={details.last_health_details} onChange={() => {}} t={t} /></div></div>
          {details.custom_domain && <div className="rounded-xl border border-gray-200 p-4"><p className="font-semibold text-gray-800">{details.custom_domain}</p><p className="mt-1 text-xs text-gray-500">{t('DNS status')}: {t(details.custom_domain_status)}</p><Button size="sm" variant="secondary" className="mt-3" loading={busy === `verify-${details.public_id}`} onClick={verifySelectedDomain}>{t('Verify DNS')}</Button></div>}
          <div><div className="flex items-center justify-between gap-3"><div><h3 className="font-bold text-gray-900">{t('Recovery points')}</h3><p className="mt-1 text-xs text-gray-500">{t('Database and school media are stored together and verified with a checksum.')}</p></div><Button size="sm" variant="secondary" loading={busy === `backup-${details.public_id}`} onClick={() => performAction('backup', details)}>{t('Create backup')}</Button></div><div className="mt-3 space-y-2">{(details.backups || []).length === 0 ? <p className="rounded-xl bg-gray-50 p-4 text-center text-xs text-gray-500">{t('No recovery points yet.')}</p> : (details.backups || []).slice(0, 10).map((backup) => <div key={backup.public_id} className="flex items-center justify-between gap-3 rounded-xl border border-gray-100 p-3"><div><p className="font-medium text-gray-800">{new Date(backup.created_at).toLocaleString()}</p><p className="text-xs text-gray-400">{t(backup.kind)} · {formatBytes(backup.total_size_bytes)} · {t(backup.status)}</p>{backup.error && <p className="mt-1 text-xs text-red-600">{backup.error}</p>}</div>{backup.status === 'succeeded' && !details.uses_default_database && <Button size="sm" variant="danger" loading={busy === `restore-${backup.public_id}`} onClick={() => restoreBackup(backup)}>{t('Restore')}</Button>}</div>)}</div></div>
          <div><h3 className="font-bold text-gray-900">{t('Recent operations')}</h3><div className="mt-3 space-y-2">{(details.operations || []).slice(0, 8).map((operation) => <div key={operation.id} className="flex items-start justify-between gap-3 rounded-xl border border-gray-100 p-3"><div><p className="font-medium text-gray-800">{t(operation.operation)}</p><p className="text-xs text-gray-400">{new Date(operation.created_at).toLocaleString()}</p>{operation.error && <p className="mt-1 text-xs text-red-600">{operation.error}</p>}</div><Badge tone={operation.status === 'succeeded' ? 'green' : operation.status === 'failed' ? 'red' : 'yellow'}>{t(operation.status)}</Badge></div>)}</div></div>
        </div>}
      </Modal>

      <Modal open={plansOpen} onClose={() => !saving && setPlansOpen(false)} title={t('Plan catalogue')} size="xl">
        <div className="grid gap-6 lg:grid-cols-[1.05fr_0.95fr]">
          <div>
            <div className="mb-3 flex items-center justify-between gap-3">
              <div><h3 className="font-bold text-gray-900">{t('Available plans')}</h3><p className="mt-1 text-xs text-gray-500">{t('Templates define defaults. Existing school overrides are not changed when a template is edited.')}</p></div>
              <Button size="sm" variant="secondary" onClick={() => { setEditingPlanId(null); setPlanForm({ ...EMPTY_PLAN, features: entitlementDefaults(catalogue), quotas: normalizedQuotas({}, catalogue) }); }}>{t('New plan')}</Button>
            </div>
            <div className="max-h-[34rem] space-y-3 overflow-y-auto pe-1">
              {plans.map((plan) => (
                <div key={plan.id} className={`rounded-2xl border p-4 ${editingPlanId === plan.id ? 'border-primary-400 bg-primary-50/40' : 'border-gray-200'}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div><div className="flex flex-wrap items-center gap-2"><h4 className="font-bold text-gray-900">{plan.name}</h4><Badge tone={plan.is_active ? 'green' : 'gray'}>{t(plan.is_active ? 'active' : 'inactive')}</Badge></div><p className="mt-1 text-xs text-gray-400">{plan.code} · {plan.school_count} {t('schools')}</p></div>
                    <div className="flex gap-1"><Button size="sm" variant="ghost" onClick={() => editPlan(plan)}>{t('Edit')}</Button><Button size="sm" variant="ghost" loading={busy === `plan-${plan.id}`} disabled={plan.school_count > 0} onClick={() => deletePlan(plan)}>{t('Delete')}</Button></div>
                  </div>
                  {plan.description && <p className="mt-3 text-sm text-gray-600">{plan.description}</p>}
                  <div className="mt-3 flex flex-wrap gap-2 text-xs text-gray-500"><span>{Number(plan.max_users).toLocaleString()} {t('users')}</span><span>•</span><span>{plan.max_storage_gb} GB</span><span>•</span><span>{plan.license_days ? `${plan.license_days} ${t('days')}` : t('Unlimited')}</span></div>
                  <div className="mt-3 flex flex-wrap gap-1.5">{(catalogue.entitlements || []).filter((item) => plan.features?.[item.key] !== false).map((item) => <Badge key={item.key} tone="purple">{t(item.label)}</Badge>)}</div>
                </div>
              ))}
            </div>
          </div>
          <form onSubmit={savePlan} className="space-y-4 rounded-2xl border border-gray-200 bg-gray-50/60 p-5">
            <div><h3 className="font-bold text-gray-900">{t(editingPlanId ? 'Edit plan' : 'Create a plan')}</h3><p className="mt-1 text-xs text-gray-500">{t('Set the defaults that are copied when this plan is assigned.')}</p></div>
            <Field label={t('Plan name')} required><input required value={planForm.name} onChange={(e) => setPlanForm((value) => ({ ...value, name: e.target.value, ...(!editingPlanId && !value.code ? { code: e.target.value.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 50) } : {}) }))} className={inputClass} /></Field>
            <Field label={t('Plan code')} required hint={t('Stable internal identifier.')}><input required pattern="[a-z0-9-]+" value={planForm.code} onChange={(e) => setPlanForm((value) => ({ ...value, code: e.target.value.toLowerCase() }))} className={inputClass} /></Field>
            <Field label={t('Description')}><textarea rows="2" value={planForm.description} onChange={(e) => setPlanForm((value) => ({ ...value, description: e.target.value }))} className={inputClass} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label={t('Maximum users')}><input type="number" min="1" required value={planForm.max_users} onChange={(e) => setPlanForm((value) => ({ ...value, max_users: e.target.value }))} className={inputClass} /></Field>
              <Field label={t('Storage limit (GB)')}><input type="number" min="1" required value={planForm.max_storage_gb} onChange={(e) => setPlanForm((value) => ({ ...value, max_storage_gb: e.target.value }))} className={inputClass} /></Field>
            </div>
            <Field label={t('Licence period (days)')} hint={t('Leave empty for unlimited access.')}><input type="number" min="1" value={planForm.license_days} onChange={(e) => setPlanForm((value) => ({ ...value, license_days: e.target.value }))} className={inputClass} /></Field>
            <div><h4 className="text-sm font-bold text-gray-800">{t('Role and content limits')}</h4><p className="mt-1 text-xs text-gray-500">{t('Leave empty for unlimited access.')}</p><div className="mt-3"><QuotaFields catalogue={catalogue} quotas={planForm.quotas} onChange={(key, value) => setPlanForm((current) => ({ ...current, quotas: { ...current.quotas, [key]: value } }))} t={t} /></div></div>
            <div><h4 className="text-sm font-bold text-gray-800">{t('Included capabilities')}</h4><div className="mt-2 max-h-80 overflow-y-auto pe-1"><EntitlementFields compact catalogue={catalogue} features={planForm.features} onChange={(key, checked) => setPlanForm((value) => ({ ...value, features: { ...value.features, [key]: checked } }))} t={t} /></div></div>
            <label className="flex items-center gap-2 text-sm font-semibold text-gray-700"><input type="checkbox" checked={planForm.is_active} onChange={(e) => setPlanForm((value) => ({ ...value, is_active: e.target.checked }))} />{t('Available for assignment')}</label>
            <div className="flex justify-end gap-2"><Button type="submit" loading={saving}>{t(editingPlanId ? 'Save plan' : 'Create plan')}</Button></div>
          </form>
        </div>
      </Modal>

      <Modal open={Boolean(retrySchool)} onClose={() => !saving && setRetrySchool(null)} title={t('Retry school provisioning')}>
        <form onSubmit={retry} className="space-y-4">
          <p className="text-sm text-gray-500">{t('Enter the school administrator credentials again. The password was never stored.')}</p>
          <Field label={t('Administrator email')} required><input type="email" required value={retryCredentials.admin_email} onChange={(e) => setRetryCredentials((v) => ({ ...v, admin_email: e.target.value }))} className={inputClass} /></Field>
          <Field label={t('Temporary password')} required><input type="password" minLength="10" required value={retryCredentials.admin_password} onChange={(e) => setRetryCredentials((v) => ({ ...v, admin_password: e.target.value }))} className={inputClass} /></Field>
          <div className="flex justify-end gap-3"><Button variant="secondary" onClick={() => setRetrySchool(null)}>{t('Cancel')}</Button><Button type="submit" loading={saving}>{t('Retry')}</Button></div>
        </form>
      </Modal>
    </div>
  );
}
